/**
 * Keeps personal data out of the URLs Application Insights sends (CC-17).
 *
 * <b>The staff console needs this even though its search term is not in the
 * URL.</b> M1-33 deliberately kept the tenant search out of the address bar,
 * and `Tenants.test.tsx` pins that. It is a real protection against the
 * `enableAutoRouteTracking` page-view leak — and it does nothing about the
 * other one. The SDK auto-instruments `fetch` and `XHR`
 * (`disableFetchTracking` and `disableAjaxTracking` both default to FALSE) and
 * puts the absolute request URL on the dependency item, so
 * `GET /api/v1/admin/tenants?q=…` reached App Insights regardless of what the
 * address bar said.
 *
 * The rule is the one on M1-31/32/33: "identifiers and a persona, never a
 * customer's name, email or address". A tenant's business name is a customer's
 * name.
 *
 * <b>Why an allowlist and not a denylist.</b> The SDK's own redaction
 * (`redactUrls`/`redactQueryParams`) matches param names against a list of
 * known-sensitive ones and fails open on anything new. This inverts it: a
 * param's value survives only because its name is on SAFE_QUERY_PARAMS.
 *
 * Path segments are deliberately untouched. `/tenants/{id}` is an identifier,
 * which the rule permits by name, and it is the link that gets pasted into a
 * ticket.
 *
 * This is a near-twin of `web/src/lib/telemetryRedaction.ts`, kept separate
 * for the same reason `telemetry.ts` already is (OD-18: two apps, not one) —
 * and because the allowlist is genuinely per-app. This console sends almost no
 * query parameters, and the list should stay that short.
 */
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';

/**
 * Query parameters whose values may be sent as-is.
 *
 * Paging only, today. `q` is absent, which is the point: it is M1-33's tenant
 * search term, a half-remembered business name typed off a phone call.
 */
const SAFE_QUERY_PARAMS: ReadonlySet<string> = new Set(['page', 'pageSize']);

/** What a redacted value is replaced with. Matches the SDK's own wording. */
const REDACTED = 'REDACTED';

/**
 * The same URL with every non-allowlisted query value replaced.
 *
 * Parsed by hand rather than through `URL`/`URLSearchParams` because those
 * re-encode as a side effect (`+` becomes `%20`, and so on), which would make
 * the telemetry disagree with the request that was actually sent.
 *
 * Accepts a bare URL or one with a method prefix ("GET https://…"), because
 * that is the shape of a dependency's `name`.
 */
export function redactUrlQuery(url: string): string {
  const space = url.indexOf(' ');
  if (space !== -1) {
    return url.slice(0, space + 1) + redactUrlQuery(url.slice(space + 1));
  }

  const questionMark = url.indexOf('?');
  if (questionMark === -1) {
    return url;
  }

  const base = url.slice(0, questionMark + 1);
  let query = url.slice(questionMark + 1);
  let fragment = '';
  const hash = query.indexOf('#');
  if (hash !== -1) {
    fragment = query.slice(hash);
    query = query.slice(0, hash);
  }

  const redacted = query
    .split('&')
    .map((pair) => {
      if (!pair) return pair;
      const equals = pair.indexOf('=');
      // A bare flag (`?debug`) has no value to leak, so it survives as-is.
      if (equals === -1) return pair;
      const rawName = pair.slice(0, equals);
      let name = rawName;
      try {
        name = decodeURIComponent(rawName);
      } catch {
        // A malformed escape is not a reason to fail open — match on the raw
        // name instead, which simply will not be on the allowlist.
      }
      return SAFE_QUERY_PARAMS.has(name) ? pair : `${rawName}=${REDACTED}`;
    })
    .join('&');

  return base + redacted + fragment;
}

/**
 * The App Insights telemetry initializer. Registered by `initTelemetry`.
 *
 * Fields are addressed by telemetry type rather than by sweeping every string
 * on the item, so a page title or an exception message containing a `?` is not
 * quietly rewritten. The guard against the SDK adding another URL-bearing
 * field later is `telemetryRedaction.test.ts`, which walks the WHOLE sent item.
 *
 * Always returns `true`: this filters what an item says, never whether it is
 * sent — and it cannot throw, because losing telemetry to a bug in here would
 * be a worse outcome than the one it prevents.
 */
export function redactTelemetryUrls(item: ITelemetryItem): boolean {
  try {
    const baseData = item.baseData;
    if (!baseData) return true;

    switch (item.baseType) {
      case 'PageviewData':
      case 'PageviewPerformanceData':
        redactField(baseData, 'uri');
        redactField(baseData, 'refUri');
        break;
      case 'RemoteDependencyData':
        // `target` is the absolute URL, `name` is "GET " + that URL, and
        // `data` is the URL again when the SDK populates it. All three.
        redactField(baseData, 'target');
        redactField(baseData, 'name');
        redactField(baseData, 'data');
        break;
      default:
        break;
    }
  } catch {
    // Deliberately swallowed — see above.
  }
  return true;
}

function redactField(baseData: Record<string, unknown>, field: string): void {
  const value = baseData[field];
  if (typeof value === 'string') {
    baseData[field] = redactUrlQuery(value);
  }
}
